import { Button, Flex, message, Modal } from "antd";
import { Form } from "antd";
import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { PREFIX } from "@/pages/Expenses/RecordExpense/constants";
import useExpenseDescription from "@/pages/Expenses/RecordExpense/hooks/useExpenseDescription";
import useExpenseSupplier from "@/pages/Expenses/RecordExpense/hooks/useExpenseSupplier";
import { Expense } from "@/types/expense";
import { EditExpenseOperationFlags } from "@/types/expense";
import { useUpdateExpense } from "../../hooks/useExpense";
import { useGetInputs } from "@/pages/Expenses/RecordExpense/hooks/useExpenseDraftForm";
import {
  getFlag,
  getModalConfig,
} from "@/pages/Expenses/RecordExpense/utils/expenseDraftModal";
import { LabeledValue } from "antd/es/select";
import { UpdateExpenseDescriptionFormValues } from "@/pages/Expenses/ManageExpense/api/expenseDescriptions";
import { useUpdateExpenseDescription } from "@/pages/Expenses/ManageExpense/hooks/useExpenseDescriptions";

interface ExpenseEditModalProps {
  data?: Expense;
  isOpen: boolean;
  onClose: () => void;
  onRefetch: () => void;
}

export interface EditExpenseFormValues {
  id: string;
  user_id: number;
  expense_type_id: string;
  date: Dayjs;
  description_id?: string | LabeledValue;
  supplier_id?: string | LabeledValue;
  price: number;
  quantity?: number;
  unit_id?: string;
  staff_name?: string;
}

const ExpenseEditModal = ({
  data,
  isOpen,
  onClose,
  onRefetch,
}: ExpenseEditModalProps) => {
  const [form] = Form.useForm<EditExpenseFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const { mutate: updateExpense } = useUpdateExpense();

  // Used to store updates for the expense description, specifically when the description's unit_id is not set and needs to be updated
  const [descriptionUpdates, setDescriptionUpdates] =
    useState<UpdateExpenseDescriptionFormValues>();

  const inputs = useGetInputs({
    form,
    expenseType: data?.expense_type,
    setDescriptionUpdates,
  });

  const { mutate: updateExpenseDescription } = useUpdateExpenseDescription();
  const { handleAddExpenseDescription, handleReactivateExpenseDescription } =
    useExpenseDescription();
  const { handleAddExpenseSupplier, handleReactivateExpenseSupplier } =
    useExpenseSupplier();

  // handle updating expense with the flags
  const handleUpdate = async (
    values: EditExpenseFormValues,
    flags: EditExpenseOperationFlags
  ) => {
    const onCloseConfirmatioModal = () => {
      setSubmitting(false);
      Modal.destroyAll();
    };

    const description_id =
      typeof values.description_id === "string"
        ? values.description_id
        : values.description_id?.value.toString();
    const supplier_id =
      typeof values.supplier_id === "string"
        ? values.supplier_id
        : values.supplier_id?.value.toString();

    // Create a working copy of values for processing
    let workingValues = {
      ...values,
      description_id: description_id,
      supplier_id: supplier_id,
    };

    if (flags.isNewDescription) {
      await handleAddExpenseDescription({
        values: {
          ...workingValues,
          date: workingValues.date.toISOString(),
        },
        onSuccess: (description) => {
          workingValues.description_id = description.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    } else if (flags.isReactivateDescription) {
      await handleReactivateExpenseDescription({
        values: {
          ...workingValues,
          date: workingValues.date.toISOString(),
        },
        onSuccess: (description) => {
          workingValues.description_id = description.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    if (flags.isNewSupplier) {
      await handleAddExpenseSupplier({
        values: {
          ...workingValues,
          date: workingValues.date.toISOString(),
        },
        onSuccess: (supplier) => {
          workingValues.supplier_id = supplier.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    } else if (flags.isReactivateSupplier) {
      await handleReactivateExpenseSupplier({
        values: {
          ...workingValues,
          date: workingValues.date.toISOString(),
        },
        onSuccess: (supplier) => {
          workingValues.supplier_id = supplier.id;
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    if (descriptionUpdates) {
      updateExpenseDescription(descriptionUpdates, {
        onSuccess: () => {
          message.success("Successfully updated expense description");
          onCloseConfirmatioModal();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    // Update expense via API
    if (data?.id) {
      updateExpense(
        { id: data.id, payload: workingValues },
        {
          onSuccess: () => {
            form.resetFields();
            setSubmitting(false);
            message.success("Successfully updated expense");
            onClose();
            onRefetch();
          },
          onError: () => {
            setSubmitting(false);
          },
        }
      );
    }
  };

  const handleFinish = async (values: EditExpenseFormValues) => {
    setSubmitting(true);

    const description_id =
      typeof values.description_id === "string"
        ? values.description_id
        : values.description_id?.value.toString();
    const supplier_id =
      typeof values.supplier_id === "string"
        ? values.supplier_id
        : values.supplier_id?.value.toString();

    const payload: EditExpenseFormValues = {
      ...values,
      description_id: description_id,
      supplier_id: supplier_id,
      date: values.date,
    };

    const flags: EditExpenseOperationFlags = {
      isNewDescription: getFlag(payload.description_id, PREFIX.NEW),
      isNewSupplier: getFlag(payload.supplier_id, PREFIX.NEW),
      isReactivateDescription: getFlag(payload.description_id, PREFIX.INACTIVE),
      isReactivateSupplier: getFlag(payload.supplier_id, PREFIX.INACTIVE),
    };

    const modalConfig = getModalConfig(flags);

    try {
      if (modalConfig) {
        Modal.confirm({
          title: modalConfig.title,
          content: modalConfig.content,
          okText: "Yes",
          cancelText: "Cancel",
          onOk: () => handleUpdate(payload, flags),
          onCancel: () => setSubmitting(false),
        });
      } else {
        handleUpdate(payload, flags);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen && data) {
      const isDescriptionActive = data?.description?.is_active;
      const isSupplierActive = data?.supplier?.is_active;
      const isUnitActive = data?.unit?.is_active;

      const description_id = isDescriptionActive
        ? data?.description_id
        : {
            key: `${PREFIX.INACTIVE}${data?.description_id}`,
            value: `${PREFIX.INACTIVE}${data?.description_id}`,
            label: data?.description?.name,
          };
      const supplier_id = isSupplierActive
        ? data?.supplier_id
        : {
            key: `${PREFIX.INACTIVE}${data?.supplier_id}`,
            value: `${PREFIX.INACTIVE}${data?.supplier_id}`,
            label: data?.supplier?.name,
          };
      const unit_id = isUnitActive ? data?.unit_id : undefined;

      form.setFieldsValue({
        id: data?.id,
        user_id: Number(data?.user_id),
        expense_type_id: data?.expense_type_id,
        date: dayjs(data?.date),
        description_id: description_id,
        supplier_id: supplier_id,
        price: data?.price,
        quantity: data?.quantity,
        unit_id: unit_id,
        staff_name: data?.staff_name,
      });
      return;
    }

    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title="Edit Expense"
      footer={null}
      centered
      destroyOnHidden
    >
      <Form<EditExpenseFormValues>
        form={form}
        onFinish={handleFinish}
        layout="vertical"
      >
        <Form.Item name="id" hidden />
        <Form.Item name="date" hidden />
        <Form.Item name="user_id" hidden />
        <Form.Item name="expense_type_id" hidden />

        {inputs.map((input) => (
          <Form.Item
            {...input.itemProps}
            labelCol={{ style: { paddingBottom: 4 } }}
            key={input.itemProps.name}
          >
            {input.component}
          </Form.Item>
        ))}

        <Flex justify="flex-end" gap={16} align="center">
          <Button variant="filled" onClick={onClose}>
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="default"
            variant="solid"
            color="primary"
            loading={submitting}
          >
            Update Expense
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default ExpenseEditModal;
